document.addEventListener("DOMContentLoaded", function () {
  const isMobile = window.matchMedia("(max-width: 749px)");

  function init() {
    document.querySelectorAll(".hover-images-on, .hover-images-v2").forEach((section) => {
      if (!(section instanceof HTMLElement)) {
        return;
      }

      if (section.dataset.mobileStickyImageInitialized === "true") {
        return;
      }

      section.dataset.mobileStickyImageInitialized = "true";

      const mobileBackground =
        section.querySelector(".group-block:first-of-type .group-block__media-wrapper") ||
        section.querySelector(".custom-section-background.desktop\\:hidden");

      if (!(mobileBackground instanceof HTMLElement)) {
        return;
      }

      const mobileBackgroundElement = mobileBackground;

      let sectionVisible = false;
      const sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            sectionVisible = entry.isIntersecting;
            mobileBackgroundElement.style.visibility = sectionVisible ? "visible" : "hidden";
          });
        },
        { root: null, threshold: 0 }
      );

      sectionObserver.observe(section);

      const images = Array.from(
        mobileBackground.querySelectorAll(".background-image-container img")
      ).filter((img) => img instanceof HTMLImageElement);

      if (!images.length) {
        return;
      }

      /** @type {HTMLElement[]} */
      const allItems = Array.from(section.querySelectorAll("[data-image]")).filter(
        (el) => el instanceof HTMLElement
      );

      // Ignore wrapper blocks that contain other triggers - only the leaf
      // blocks take part in the scroll-position math.
      const items = allItems.filter(
        (el) => !allItems.some((other) => other !== el && el.contains(other))
      );

      if (!items.length) {
        return;
      }

      // The sticky block that holds the background image on mobile.
      const stickyBlock = mobileBackgroundElement.closest(".group-block:not([data-image])");

      /** @type {Set<string>} */
      const loadedSources = new Set();

      /** @type {Map<string, Promise<string>>} */
      const pendingSources = new Map();

      /** @type {ReturnType<typeof setTimeout> | null} */
      let preloadTimeout = null;

      let activationRequestId = 0;

      /** @type {HTMLElement | null} */
      let currentItem = null;

      /**
       * @param {HTMLElement} item
       */
      function sourceFor(item) {
        const mobileSrc = item.dataset.mobileImage;
        const fallbackSrc = item.dataset.image;
        const src = mobileSrc && mobileSrc.length ? mobileSrc : fallbackSrc;
        return typeof src === "string" ? src : "";
      }

      /**
       * @param {string} src
       */
      function applyImages(src) {
        images.forEach((img) => {
          img.style.transition = "none";
          img.style.opacity = "1";

          if (img.src === src || img.currentSrc === src) {
            return;
          }

          img.src = src;
          img.srcset = src;
        });
      }

      /**
       * @param {string} src
       */
      function ensureSourceLoaded(src) {
        if (!src || src.includes("no-image-2048")) {
          return Promise.resolve("");
        }

        if (loadedSources.has(src)) {
          return Promise.resolve(src);
        }

        const pending = pendingSources.get(src);
        if (pending) {
          return pending;
        }

        const promise = new Promise((resolve) => {
          const preload = new Image();
          preload.decoding = "async";
          preload.src = src;
          preload.onload = () => {
            loadedSources.add(src);
            pendingSources.delete(src);
            resolve(src);
          };
          preload.onerror = () => {
            pendingSources.delete(src);
            resolve(src);
          };
        });

        pendingSources.set(src, promise);
        return promise;
      }

      /**
       * @param {HTMLElement} item
       */
      function applyImageFor(item) {
        if (!(item instanceof HTMLElement)) {
          return;
        }

        items.forEach((i) => {
          const active = i === item;
          i.classList.toggle("active__", active);
          i.classList.toggle("is-active", active);
          i.setAttribute("aria-current", active ? "true" : "false");
        });

        const src = sourceFor(item);
        if (!src || src.includes("no-image-2048")) {
          return;
        }

        activationRequestId += 1;
        const requestId = activationRequestId;

        ensureSourceLoaded(src).then(() => {
          if (requestId !== activationRequestId) {
            return;
          }

          applyImages(src);
        });
      }

      function preloadRemainingSources() {
        items.forEach((item) => {
          const src = sourceFor(item);
          if (!src || src.includes("no-image-2048")) {
            return;
          }

          ensureSourceLoaded(src);
        });
      }

      function scheduleRemainingPreload() {
        if (preloadTimeout) {
          clearTimeout(preloadTimeout);
        }

        preloadTimeout = setTimeout(() => {
          preloadRemainingSources();
          preloadTimeout = null;
        }, 150);
      }

      let ticking = false;

      /**
       * Viewport Y of the bottom edge of the sticky image. Text blocks scroll
       * underneath this edge, so it is the line an item has to cross before it
       * becomes the active one.
       */
      function getStickyBottom() {
        if (stickyBlock instanceof HTMLElement) {
          const rect = stickyBlock.getBoundingClientRect();
          if (rect.height > 0) {
            return rect.bottom;
          }
        }

        const bgImage = mobileBackgroundElement.querySelector(".background-image-container");
        const headerHeight = 70;
        return headerHeight + (bgImage instanceof HTMLElement ? bgImage.offsetHeight : 290);
      }

      /**
       * Viewport Y of the border between two consecutive blocks. Uses the
       * divider line when one sits between them, otherwise the middle of the
       * gap separating the two blocks.
       * @param {HTMLElement} prev
       * @param {HTMLElement} next
       */
      function getBorderY(prev, next) {
        let node = prev.nextElementSibling;

        while (node && node !== next) {
          if (node instanceof HTMLElement && node.matches(".divider")) {
            const line = node.querySelector(".divider__line") || node;
            const lineRect = line.getBoundingClientRect();
            return lineRect.top + lineRect.height / 2;
          }

          node = node.nextElementSibling;
        }

        const prevRect = prev.getBoundingClientRect();
        const nextRect = next.getBoundingClientRect();
        return (prevRect.bottom + nextRect.top) / 2;
      }

      function computeActive() {
        ticking = false;
        if (!sectionVisible) {
          return;
        }

        const stickyBottom = getStickyBottom();
        let candidate = items[0] || null;

        for (let i = 1; i < items.length; i += 1) {
          // Only switch to the next block once the border between it and the
          // previous block has scrolled underneath the sticky image.
          const border = getBorderY(items[i - 1], items[i]);

          if (border <= stickyBottom) {
            candidate = items[i];
          } else {
            break;
          }
        }

        if (candidate && candidate !== currentItem) {
          currentItem = candidate;
          applyImageFor(candidate);
        }
      }

      function onScroll() {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(computeActive);
        }
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);

      const initial = section.querySelector(".group-block.active__") || items[0] || null;

      if (initial instanceof HTMLElement) {
        currentItem = initial;
        applyImageFor(initial);
        scheduleRemainingPreload();
      } else {
        scheduleRemainingPreload();
      }

      onScroll();
    });
  }

  if (isMobile.matches) {
    init();
  }

  isMobile.addEventListener("change", (e) => {
    if (e.matches) {
      init();
    }
  });
});
